import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Api, { formatUrl } from '@plone/volto/helpers/Api/Api';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { Message } from 'semantic-ui-react';
import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';
import { listActions, getTypes } from '@plone/volto/actions';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import { useClient } from '@plone/volto/hooks/client/useClient';

/**
 * Tela "Minhas Pendências" — lista os conteúdos do usuário logado que estão
 * com pendência de acessibilidade (``a11y_pendente=True``), ordenados pelo
 * prazo de correção mais próximo.
 *
 * Consome ``GET /@a11y-minhas-pendencias`` (serviço registrado no site root
 * pelo add-on v2inclusiva.a11y) e renderiza uma lista com nota, situação e
 * prazo, com link direto para a edição de cada item.
 */

// O `mod` casa com os modificadores de `.mpf-a11y-pill` em
// `theme/styles/components/internal/_a11y.scss`.
const STATUS = {
  conforme: { label: 'Conforme', mod: 'aprovado' },
  pendente: { label: 'Pendente', mod: 'ressalva' },
  em_analise: { label: 'Em análise', mod: 'analise' },
  em_correcao: { label: 'Em correção', mod: 'analise' },
};

const pillClass = (mod) => `mpf-a11y-pill mpf-a11y-pill--${mod}`;

function formatPrazo(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
}

function notaLabel(nota) {
  if (typeof nota !== 'number') return '—';
  return nota.toFixed(1);
}

const MinhasPendencias = ({ escopo = 'autor' }) => {
  const legado = escopo === 'legado';
  const { pathname } = useLocation();
  const isClient = useClient();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userSession.token);
  const [loading, setLoading] = useState(true);

  // Carrega as ações/tipos da raiz para o Toolbar (a rota não é conteúdo e o
  // Toolbar, sozinho, acha que o expander já trouxe — então não busca).
  useEffect(() => {
    if (token) {
      dispatch(listActions(''));
      dispatch(getTypes(''));
    }
  }, [token, dispatch]);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = new Api();
      // O serviço é registrado no site root, então o URL é /++api++/@a11y-minhas-pendencias.
      const base = `${formatUrl('/')}@a11y-minhas-pendencias`;
      const url = legado ? `${base}?escopo=legado` : base;
      const res = await api.get(url);
      setItems(Array.isArray(res?.items) ? res.items : []);
    } catch (err) {
      const body = err?.response?.body;
      const message =
        body?.message ||
        body?.error?.message ||
        err?.message ||
        'Não foi possível carregar suas pendências de acessibilidade.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [legado]);

  useEffect(() => {
    if (token) {
      load();
    } else {
      setLoading(false);
    }
  }, [token, load]);

  if (!token) {
    return (
      <div className="mpf-a11y-page">
        <div className="mpf-a11y-empty">
          <p className="mpf-a11y-empty__title">
            {legado
              ? 'Você precisa entrar para ver as pendências legadas'
              : 'Você precisa entrar para ver suas pendências'}
          </p>
          <p className="mpf-a11y-empty__text">
            Faça login para acompanhar os conteúdos que exigem correção de
            acessibilidade.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mpf-a11y-page">
      <div className="mpf-a11y-page__head">
        <h1 className="mpf-a11y-page__title">
          {legado ? '♿ Pendências Legadas' : '♿ Minhas Pendências'}
        </h1>
        {!loading && !error && (
          <span className="mpf-a11y-page__count">{items.length}</span>
        )}
        <div className="mpf-a11y-page__spacer" />
        <MPFButton
          variant="outline"
          size="m"
          loading={loading}
          disabled={loading}
          onClick={load}
          aria-label="Atualizar pendências"
        >
          Atualizar
        </MPFButton>
      </div>
      <p className="mpf-a11y-page__subtitle">
        {legado
          ? 'Conteúdos do acervo legado (migrado) das suas unidades que precisam de correção de acessibilidade.'
          : 'Conteúdos de sua autoria que precisam de correção de acessibilidade antes de publicar.'}
      </p>

      {error && (
        <Message negative>
          <Message.Header>Falha ao carregar</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="mpf-a11y-empty">
          <p className="mpf-a11y-empty__title">
            {legado ? 'Nenhuma pendência legada 🎉' : 'Nenhuma pendência 🎉'}
          </p>
          <p className="mpf-a11y-empty__text">
            {legado
              ? 'O acervo legado das suas unidades está em dia com a acessibilidade.'
              : 'Todos os seus conteúdos estão em dia com a acessibilidade.'}
          </p>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="mpf-a11y-list">
          {items.map((item) => {
            const status = STATUS[item.a11y_status] || null;
            const appPath = flattenToAppURL(item['@id']);
            return (
              <div key={item['@id']} className="mpf-a11y-item">
                <div>
                  <UniversalLink
                    href={`${appPath}/edit`}
                    className="mpf-a11y-item__title"
                  >
                    {item.title || item['@id']}
                  </UniversalLink>
                  <div className="mpf-a11y-item__meta">
                    <span className="mpf-a11y-item__meta-label">
                      Situação:{' '}
                      <span className="mpf-a11y-item__meta-value">
                        {item.review_state || '—'}
                      </span>
                    </span>
                    <span className="mpf-a11y-item__meta-label">
                      Prazo:{' '}
                      <span className="mpf-a11y-item__meta-value">
                        {formatPrazo(item.a11y_prazo)}
                      </span>
                    </span>
                  </div>
                  <div className="mpf-a11y-item__review">
                    <UniversalLink
                      href={`${appPath}/edit`}
                      className="mpf-a11y-item__review-link"
                    >
                      Revisar →
                    </UniversalLink>
                  </div>
                </div>
                <div className="mpf-a11y-item__score-wrap">
                  {status && (
                    <span className={pillClass(status.mod)}>
                      {status.label}
                    </span>
                  )}
                  <div className="mpf-a11y-item__score">
                    <span className="mpf-a11y-item__score-num">
                      {notaLabel(item.a11y_nota)}
                    </span>
                    <span className="mpf-a11y-item__score-label">nota</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isClient &&
        createPortal(
          <Toolbar pathname={pathname} inner={<span />} />,
          document.getElementById('toolbar'),
        )}
    </div>
  );
};

MinhasPendencias.propTypes = {
  escopo: PropTypes.oneOf(['autor', 'legado']),
};

export default MinhasPendencias;
